import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { AuthUserService } from '@/services/AuthUserService'

interface UserRolesContextType {
  currentRoles: string[]
  currentChannelPartnerId: number
  loading: boolean
}

const UserRolesContext = createContext<UserRolesContextType | undefined>(undefined)

interface UserRolesProviderProps {
  children: ReactNode
}

export function UserRolesProvider({ children }: UserRolesProviderProps) {
  const [currentRoles, setCurrentRoles] = useState<string[]>([])
  const [currentChannelPartnerId, setCurrentChannelPartnerId] = useState<number>(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    const fetchUserData = async () => {
      try {
        setLoading(true)
        const currentUser = await AuthUserService.getUser()
        
        if (active) { 
          const result = (await AuthUserService.getRoles()).roles;
          setCurrentRoles(result.filter(role => currentUser.roles.includes(role)) || [])
          setCurrentChannelPartnerId(currentUser.partnerId || 0)
        }
      } catch (error) {
        console.error('Failed to fetch current user data', error)
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchUserData()
    return () => { active = false }
  }, [])

  const value = useMemo(() => ({
    currentRoles,
    currentChannelPartnerId,
    loading
  }), [currentRoles, currentChannelPartnerId, loading])

  return (
    <UserRolesContext.Provider value={value}>
      {children}
    </UserRolesContext.Provider>
  )
}

export function useUserRoles() {
  const context = useContext(UserRolesContext)
  if (context === undefined) {
    throw new Error('useUserRoles must be used within a UserRolesProvider')
  }
  return context
}